'use client'

import { motion } from 'framer-motion'
import { ArrowRight, Sparkles } from 'lucide-react'
import Link from 'next/link'
import dynamic from 'next/dynamic'

const FloatingLines = dynamic(() => import('@/components/FloatingLines'), {
  ssr: false,
})

export function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-background py-20 md:py-32">
      {/* Floating Lines Animated Background */}
      <div className="absolute inset-0 z-0">
        <FloatingLines
          enabledWaves={['top', 'middle', 'bottom']}
          lineCount={[10, 15, 20]}
          lineDistance={[8, 6, 4]}
          bendRadius={5.0}
          bendStrength={-0.5}
          interactive={true}
          parallax={true}
        />
      </div>

      <div className="container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6"
          >
            <Sparkles className="w-4 h-4 mr-2 text-primary" />
            <span className="text-sm font-medium text-primary">
              Premium Knowledge Hub
            </span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 1, y: 0 }} // Optimized for LCP: Visible immediately
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
          >
            <span className="gradient-text-primary">Premium Insights</span>
            <br />
            for Modern Professionals
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed"
          >
            Explore research-backed articles on web development, design, technology, lifestyle, and business.
            <span className="text-foreground font-medium"> Expert knowledge, practical insights.</span>
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              href="/blog"
              className="group inline-flex items-center px-8 py-4 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Explore Articles
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>

            <Link
              href="/newsletter"
              className="inline-flex items-center px-8 py-4 rounded-xl bg-background border-2 border-gray-200 dark:border-gray-800 font-semibold hover:bg-muted transition-all duration-200"
            >
              Subscribe to Newsletter
            </Link>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="grid grid-cols-3 gap-8 mt-16 max-w-2xl mx-auto"
          >
            <div>
              <div className="text-3xl md:text-4xl font-bold gradient-text-primary"> 10 </div>
              <div className="text-sm text-muted-foreground mt-1">Articles Every Week</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold gradient-text-primary">5</div>
              <div className="text-sm text-muted-foreground mt-1">Categories</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold gradient-text-primary">2.0k+</div>
              <div className="text-sm text-muted-foreground mt-1">Avg Words</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
